/**
 * Version Checker - Auto reload when new version is deployed
 * Enhanced for PWA auto-update on both iOS and Android
 */

const VERSION_CHECK_INTERVAL = 2 * 60 * 1000 // Check every 2 minutes
const VERSION_URL = '/version.json'
const STORAGE_KEY = 'app_version'
const LAST_CHECK_KEY = 'app_version_last_check'

class VersionChecker {
  constructor() {
    this.currentVersion = null
    this.intervalId = null
    this.isReloading = false
  }

  /**
   * Initialize version checker
   */
  async init() {
    try {
      // Register for PWA update events
      this.registerPWAUpdateHandler()

      // Get initial version
      const versionData = await this.fetchVersion()
      if (versionData) {
        this.currentVersion = versionData.version

        // Check stored version
        const storedVersion = localStorage.getItem(STORAGE_KEY)
        if (storedVersion && storedVersion !== this.currentVersion) {
          console.log(`🔄 New version detected on load: ${storedVersion} -> ${this.currentVersion}`)
          // Auto update on app open (user just opened the app, safe to reload)
          this.autoUpdate(versionData.version)
          return
        }

        // Store current version
        localStorage.setItem(STORAGE_KEY, this.currentVersion)
        localStorage.setItem(LAST_CHECK_KEY, Date.now().toString())
        console.log(`📦 App version: ${this.currentVersion}`)
      }

      // Start periodic check
      this.startPeriodicCheck()

      // Check service worker for updates
      this.checkServiceWorkerUpdate()
    } catch (error) {
      console.error('Version check failed:', error)
    }
  }

  /**
   * Register PWA update handler
   */
  registerPWAUpdateHandler() {
    if ('serviceWorker' in navigator) {
      // When a new service worker takes control, reload to use new assets
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (this.isReloading) return
        console.log('🔄 Service Worker updated, reloading...')
        this.isReloading = true
        window.location.reload()
      })

      // Listen for new service worker waiting
      navigator.serviceWorker.ready.then(registration => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('🔄 New version available, activating...')
                // Skip waiting and activate immediately
                newWorker.postMessage({ type: 'SKIP_WAITING' })
              }
            })
          }
        })
      })
    }
  }

  /**
   * Check for service worker updates
   */
  async checkServiceWorkerUpdate() {
    if ('serviceWorker' in navigator) {
      try {
        const registration = await navigator.serviceWorker.ready
        await registration.update()
        console.log('📦 Service Worker update checked')
      } catch (error) {
        console.log('Service Worker update check failed:', error)
      }
    }
  }

  /**
   * Fetch version from server
   */
  async fetchVersion() {
    try {
      // Add cache-busting query string
      const response = await fetch(`${VERSION_URL}?t=${Date.now()}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      })

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`)
      }

      return await response.json()
    } catch (error) {
      console.error('Failed to fetch version:', error)
      return null
    }
  }

  /**
   * Start periodic version check
   */
  startPeriodicCheck() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
    }

    this.intervalId = setInterval(async () => {
      await this.checkForUpdate()
      await this.checkServiceWorkerUpdate()
    }, VERSION_CHECK_INTERVAL)

    // Also check when tab becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Check if it's been more than 1 minute since last check
        const lastCheck = parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0')
        if (Date.now() - lastCheck > 60000) {
          this.checkForUpdate()
          this.checkServiceWorkerUpdate()
        }
      }
    })

    // Also check on online event (when connection restored)
    window.addEventListener('online', () => {
      console.log('🌐 Connection restored, checking for updates...')
      this.checkForUpdate()
      this.checkServiceWorkerUpdate()
    })

    // Check on focus (for PWA)
    window.addEventListener('focus', () => {
      const lastCheck = parseInt(localStorage.getItem(LAST_CHECK_KEY) || '0')
      if (Date.now() - lastCheck > 60000) {
        this.checkForUpdate()
      }
    })
  }

  /**
   * Check if there's a new version
   */
  async checkForUpdate() {
    try {
      localStorage.setItem(LAST_CHECK_KEY, Date.now().toString())
      const versionData = await this.fetchVersion()
      if (versionData && versionData.version !== this.currentVersion) {
        console.log(`🔄 Update available: ${this.currentVersion} -> ${versionData.version}`)
        this.showUpdateNotification(versionData.version)
      }
    } catch (error) {
      // Silently fail on periodic checks
    }
  }

  /**
   * Auto update without prompt (for initial load when version already changed)
   */
  autoUpdate(newVersion) {
    console.log(`🚀 Auto updating to version ${newVersion}...`)
    localStorage.setItem(STORAGE_KEY, newVersion)
    this.clearCacheAndReload()
  }

  /**
   * Show update notification banner
   */
  showUpdateNotification(newVersion) {
    // Don't show if already showing
    if (document.getElementById('update-banner')) return

    // Store new version
    localStorage.setItem(STORAGE_KEY, newVersion)

    // Make versionChecker accessible globally for the button
    window.versionChecker = this

    // Create update banner
    const banner = document.createElement('div')
    banner.id = 'update-banner'
    banner.innerHTML = `
      <div style="
        position: fixed;
        bottom: 80px;
        left: 50%;
        transform: translateX(-50%);
        background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
        color: white;
        padding: 12px 24px;
        border-radius: 12px;
        box-shadow: 0 4px 20px rgba(0,0,0,0.3);
        z-index: 99999;
        display: flex;
        align-items: center;
        gap: 12px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        max-width: 90vw;
        flex-wrap: wrap;
        justify-content: center;
      ">
        <span style="font-size: 14px;">มีเวอร์ชันใหม่ ${newVersion}</span>
        <button onclick="window.versionChecker.clearCacheAndReload()" style="
          background: white;
          color: #2563eb;
          border: none;
          padding: 8px 16px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          cursor: pointer;
        ">อัปเดตเลย</button>
        <button onclick="document.getElementById('update-banner').remove()" style="
          background: transparent;
          color: white;
          border: 1px solid rgba(255,255,255,0.3);
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 13px;
          cursor: pointer;
        ">ภายหลัง</button>
      </div>
    `
    document.body.appendChild(banner)
  }

  /**
   * Clear cache and reload
   * Note: Do NOT unregister service workers - VitePWA handles SW lifecycle.
   * Unregistering would break PWA functionality (standalone mode, offline, etc.)
   */
  async clearCacheAndReload() {
    if (this.isReloading) return
    this.isReloading = true

    try {
      // Clear runtime caches (but keep SW registered)
      if ('caches' in window) {
        const cacheNames = await caches.keys()
        await Promise.all(
          cacheNames.map(cacheName => caches.delete(cacheName))
        )
        console.log('🗑️ Caches cleared')
      }

      // Trigger SW update check so new SW is fetched
      if ('serviceWorker' in navigator) {
        const registration = await navigator.serviceWorker.ready
        await registration.update()
      }

      // Reload the page
      window.location.reload()
    } catch (error) {
      console.error('Failed to clear cache:', error)
      // Fallback: just reload
      window.location.reload()
    }
  }

  /**
   * Stop version checker
   */
  stop() {
    if (this.intervalId) {
      clearInterval(this.intervalId)
      this.intervalId = null
    }
  }

  /**
   * Force check and update now
   */
  async forceUpdate() {
    localStorage.removeItem(STORAGE_KEY)
    await this.clearCacheAndReload()
  }
}

export const versionChecker = new VersionChecker()
export default versionChecker
